import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { authApi } from '../lib/api';
import { FileText, ArrowLeft, Mail, CheckCircle, Key, Eye, EyeOff, ShieldAlert, Copy, Check, Download } from 'lucide-react';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('choose'); // 'choose', 'email', 'email-sent', 'recovery', 'recovery-success'
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState({ email: '', recoveryKey: '', newPassword: '' });
  const [newRecoveryKey, setNewRecoveryKey] = useState(null);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgotPassword({ email });
      setMode('email-sent');
      toast.success('Check your email for the reset link');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (recoveryForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.recoveryReset({
        email: recoveryForm.email,
        recovery_key: recoveryForm.recoveryKey,
        new_password: recoveryForm.newPassword,
      });
      setNewRecoveryKey(res.data.new_recovery_key);
      setMode('recovery-success');
      toast.success('Password reset successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or recovery key';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(newRecoveryKey);
    setCopied(true);
    toast.success('New recovery key copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadKey = () => {
    const content = `RaiseRoot Account Recovery Key\n${'='.repeat(40)}\nEmail: ${recoveryForm.email}\nNew Recovery Key: ${newRecoveryKey}\n\nThis replaces your previous recovery key.\nStore this in a safe place.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'raiseroot-recovery-key.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Recovery success — show new key
  if (mode === 'recovery-success') {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="recovery-success-page">
        <div className="w-full max-w-lg px-4">
          <Card className="bg-white border-stone-200 shadow-card">
            <CardContent className="pt-8 pb-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-serif text-xl font-bold text-primary-900 mb-2">Password Reset Complete</h2>
                <p className="text-stone-600 text-sm">Your password has been updated. Here is your <strong>new recovery key</strong>:</p>
              </div>

              <div className="bg-stone-900 rounded-lg p-4 mb-4">
                <code className="text-green-400 text-sm font-mono break-all block" data-testid="new-recovery-key-display">
                  {newRecoveryKey}
                </code>
              </div>

              <div className="flex gap-3 mb-4">
                <Button onClick={handleCopyKey} variant="outline" className="flex-1 border-stone-300" data-testid="copy-new-key-btn">
                  {copied ? <Check className="w-4 h-4 mr-2 text-green-600" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button onClick={handleDownloadKey} variant="outline" className="flex-1 border-stone-300" data-testid="download-new-key-btn">
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <p className="text-xs text-amber-800"><strong>Important:</strong> Your old recovery key no longer works. Save this new one securely.</p>
              </div>

              <Link to="/login">
                <Button className="w-full h-11 bg-primary hover:bg-primary-800 text-white" data-testid="go-to-login-btn">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Email sent confirmation
  if (mode === 'email-sent') {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="forgot-password-sent-page">
        <div className="w-full max-w-md px-4">
          <Card className="bg-white border-stone-200 shadow-card">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-serif text-xl font-bold text-primary-900 mb-2">Check Your Email</h2>
              <p className="text-stone-600 mb-6">
                If an account exists for <span className="font-medium">{email}</span>, we've sent a password reset link.
              </p>
              <p className="text-sm text-stone-500 mb-6">
                The link will expire in 1 hour. Check your spam folder if you don't see it.
              </p>
              <div className="space-y-3">
                <Button variant="outline" onClick={() => { setMode('choose'); setEmail(''); }} className="w-full" data-testid="try-different-email-btn">
                  Try a different method
                </Button>
                <Link to="/login" className="block">
                  <Button variant="ghost" className="w-full" data-testid="back-to-login-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Recovery key form
  if (mode === 'recovery') {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="recovery-reset-page">
        <div className="w-full max-w-md px-4">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif font-semibold text-2xl text-primary-900">RaiseRoot</span>
            </Link>
          </div>

          <Card className="bg-white border-stone-200 shadow-card">
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Key className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="font-serif text-2xl text-primary-900">Reset with Recovery Key</CardTitle>
              <CardDescription className="text-stone-600">
                Enter the recovery key you saved when creating your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRecoverySubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recovery-email" className="text-stone-700">Email</Label>
                  <Input
                    id="recovery-email"
                    type="email"
                    placeholder="you@nonprofit.org"
                    value={recoveryForm.email}
                    onChange={(e) => setRecoveryForm({ ...recoveryForm, email: e.target.value })}
                    required
                    className="h-11 bg-white border-stone-200 focus:border-primary-300"
                    data-testid="recovery-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recovery-key" className="text-stone-700">Recovery Key</Label>
                  <Input
                    id="recovery-key"
                    type="text"
                    placeholder="Paste your recovery key"
                    value={recoveryForm.recoveryKey}
                    onChange={(e) => setRecoveryForm({ ...recoveryForm, recoveryKey: e.target.value })}
                    required
                    className="h-11 bg-white border-stone-200 focus:border-primary-300 font-mono text-sm"
                    data-testid="recovery-key-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-stone-700">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min 8 chars with letters & numbers"
                      value={recoveryForm.newPassword}
                      onChange={(e) => setRecoveryForm({ ...recoveryForm, newPassword: e.target.value })}
                      required
                      minLength={8}
                      className="h-11 bg-white border-stone-200 focus:border-primary-300 pr-10"
                      data-testid="recovery-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary-800 text-white"
                  disabled={loading}
                  data-testid="recovery-submit-btn"
                >
                  {loading ? <div className="w-5 h-5 spinner" /> : 'Reset Password'}
                </Button>
              </form>

              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => setMode('choose')}
                  className="text-sm text-stone-500 hover:text-stone-700 flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" /> Back
                </button>
                <Link to="/login" className="text-sm text-primary hover:underline">
                  Back to login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Choose method screen (default)
  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="forgot-password-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif font-semibold text-2xl text-primary-900">RaiseRoot</span>
          </Link>
        </div>

        <Card className="bg-white border-stone-200 shadow-card">
          <CardHeader className="text-center">
            <CardTitle className="font-serif text-2xl text-primary-900">Reset Your Password</CardTitle>
            <CardDescription className="text-stone-600">
              Choose how you'd like to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Recovery Key Option */}
            <button
              onClick={() => setMode('recovery')}
              className="w-full p-4 rounded-xl border-2 border-stone-200 hover:border-primary hover:bg-primary/5 transition-all text-left group"
              data-testid="choose-recovery-key-btn"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20">
                  <Key className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-stone-900">Use Recovery Key</h3>
                  <p className="text-xs text-stone-500 mt-0.5">Enter the recovery key you saved when you created your account</p>
                  <span className="inline-block mt-2 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">Recommended</span>
                </div>
              </div>
            </button>

            {/* Email Option */}
            <button
              onClick={() => setMode('email')}
              className="w-full p-4 rounded-xl border-2 border-stone-200 hover:border-primary hover:bg-primary/5 transition-all text-left group"
              data-testid="choose-email-reset-btn"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10">
                  <Mail className="w-5 h-5 text-stone-500 group-hover:text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-stone-900">Email Reset Link</h3>
                  <p className="text-xs text-stone-500 mt-0.5">We'll send a password reset link to your email address</p>
                </div>
              </div>
            </button>

            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-stone-600 hover:text-stone-800 inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
